import React from "react";

import { Button as ButtonMi, ButtonProps, CircularProgress } from "@mui/material";
import classNames from "classnames";

import { getBrandCustom } from "../../../utils/getCustomColor";
import useStyles from "./styles";

interface Props extends ButtonProps {
  children?: any;
  disableElevation?: boolean;
  customColor?: Color;
  loading?: boolean;
}

export type StylesProps = {
  background?: {
    color?: string;
    hoverColor?: string;
    activeColor?: string;
    disabledColor?: string;
  };
  text?: {
    color?: string;
    hoverColor?: string;
    activeColor?: string;
    disabledColor?: string;
  };
};

export enum Color {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  SECONDARY_DARK = "SECONDARY_DARK",
  SECONDARY_OUTLINED = "SECONDARY_OUTLINED",
  INFO = "INFO",
  TEXT_INFO = "TEXT_INFO",
  SUCCESS = "SUCCESS",
  SUCCESS_OUTLINED = "SUCCESS_OUTLINED",
  WARNING = "WARNING",
  ERROR = "ERROR",
  CANCEL = "CANCEL",
  NOT_SELECTED = "NOT_SELECTED",
  DARK = "DARK",
  DEFAULT = "DEFAULT",
  SEND = "SEND",
  SECONDARY_DARK_OUTLINED = "SECONDARY_DARK_OUTLINED",
}

const Button = (props: Props) => {
  const {
    children,
    customColor,
    disableElevation = true,
    className,
    loading,
    disabled,
    ...rest
  } = props;

  const getColorsByProps = () => {
    let colors: StylesProps;

    const Colors = {
      PRIMARY: {
        background: {
          color: getBrandCustom("primaryColor", "#38CFC4"),
          hoverColor: getBrandCustom("hoverColor", "#3FE3D7"),
          activeColor: getBrandCustom("pressedColor", "#31B6AC"),
          disabledColor: getBrandCustom("disabledColor", "#D0D5DD"),
        },
      },
      SECONDARY: {
        background: {
          color: getBrandCustom("primaryColor", "#308CDB"),
          hoverColor: getBrandCustom("hoverColor", "#369CF3"),
          activeColor: getBrandCustom("pressedColor", "#03569E"),
          disabledColor: getBrandCustom("disabledColor", "#D0D5DD"),
        },
      },
      SECONDARY_DARK: {
        background: {
          color: getBrandCustom("primaryColor", "#0055A4"),
          hoverColor: getBrandCustom("hoverColor", "rgba(0, 85, 164, .88)"),
          activeColor: getBrandCustom("pressedColor", "#0055A4"),
          disabledColor: getBrandCustom("disabledColor", "#E4E7EC"),
        },
      },
      SECONDARY_OUTLINED: {
        text: {
          color: getBrandCustom("primaryColor", "#308CDB"),
          hoverColor: getBrandCustom("hoverColor", "#369CF3"),
          activeColor: getBrandCustom("pressedColor", "#03569E"),
          disabledColor: getBrandCustom("disabledColor", "#667085"),
        },
        border: {
          color: getBrandCustom("primaryColor", "#308CDB"),
          hoverColor: getBrandCustom("hoverColor", "#369CF3"),
          activeColor: getBrandCustom("pressedColor", "#03569E"),
          disabledColor: getBrandCustom("disabledColor", "#D0D5DD"),
        },
        background: {
          disabledColor: "#D0D5DD",
        },
      },
      SECONDARY_DARK_OUTLINED: {
        text: {
          color: getBrandCustom("primaryColor", "#0055A4"),
          hoverColor: getBrandCustom("hoverColor", "#03569E"),
          activeColor: getBrandCustom("pressedColor", "#0055A4"),
          disabledColor: getBrandCustom("disabledColor", "#667085"),
        },
        border: {
          color: getBrandCustom("primaryColor", "#0055A4"),
          hoverColor: getBrandCustom("hoverColor", "#03569E"),
          activeColor: getBrandCustom("pressedColor", "#0055A4"),
          disabledColor: getBrandCustom("disabledColor", "#D0D5DD"),
        },
        background: {
          disabledColor: "#D0D5DD",
        },
      },
      INFO: {
        background: {
          color: "#EDF5FB",
          hoverColor: "#F6FBFF",
          activeColor: "#E8F5FF",
          disabledColor: "#D0D5DD",
        },
        text: {
          color: "#3F6280",
          hoverColor: "#3F6280",
          activeColor: "#3F6280",
          disabledColor: "#667085",
        },
      },
      TEXT_INFO: {
        text: {
          color: "#232325",
          hoverColor: "#232325",
          activeColor: "#232325",
          disabledColor: "#667085",
        },
      },
      SUCCESS: {
        background: {
          color: "#12B76A",
          hoverColor: "#6CE9A6",
          activeColor: "#12B76A",
          disabledColor: "#D0D5DD",
        },
      },
      SUCCESS_OUTLINED: {
        text: {
          color: "#12B76A",
          hoverColor: "#6CE9A6",
          activeColor: "#12B76A",
          disabledColor: "#D0D5DD",
        },
        border: {
          color: "#12B76A",
          hoverColor: "#6CE9A6",
          activeColor: "#12B76A",
          disabledColor: "#D0D5DD",
        },
      },
      ERROR: {
        background: {
          color: "#F04438",
          hoverColor: "#F97066",
          activeColor: "#D92D20",
          disabledColor: "#D0D5DD",
        },
      },
      WARNING: {
        background: {
          color: "#F79009",
          hoverColor: "#f79009b3",
          activeColor: "#F79009",
          disabledColor: "#D0D5DD",
        },
        text: {
          color: "#fff",
          hoverColor: "#fff",
          activeColor: "#fff",
          disabledColor: "#667085",
        },
      },
      CANCEL: {
        text: {
          color: "#F04438",
          hoverColor: "#F97066",
          activeColor: "#D92D20",
          disabledColor: "#667085",
        },
      },
      DARK: {
        text: {
          color: "#000000",
          hoverColor: "#000000",
          activeColor: "#000000",
          disabledColor: "#D0D5DD",
        },
      },
      SEND: {
        background: {
          color: "#fff",
          hoverColor: "#fff",
          activeColor: "#fff",
          disabledColor: "#fff",
        },
        text: {
          color: "#465869",
          hoverColor: "#465869",
          activeColor: "#465869",
          disabledColor: "#465869",
        },
      },
      NOT_SELECTED: {
        background: {
          color: "#CCDEEE",
          hoverColor: "#CCDEEE",
          activeColor: "#CCDEEE",
          disabledColor: "#D0D5DD",
        },
        text: {
          color: "#465869",
          hoverColor: "#465869",
          activeColor: "#465869",
          disabledColor: "#667085",
        },
      },
      DEFAULT: {
        text: {
          color: getBrandCustom("primaryColor", "#308CDB"),
          hoverColor: getBrandCustom("hoverColor", "#369CF3"),
          activeColor: getBrandCustom("pressedColor", "#03569E"),
          disabledColor: getBrandCustom("disabledColor", "#667085"),
        },
      },
    };

    switch (customColor) {
      case Color.PRIMARY:
        colors = Colors.PRIMARY;
        break;
      case Color.SECONDARY:
        colors = Colors.SECONDARY;
        break;
      case Color.INFO:
        colors = Colors.INFO;
        break;
      case Color.TEXT_INFO:
        colors = Colors.TEXT_INFO;
        break;
      case Color.DARK:
        colors = Colors.DARK;
        break;
      case Color.SUCCESS:
        colors = Colors.SUCCESS;
        break;
      case Color.ERROR:
        colors = Colors.ERROR;
        break;
      case Color.WARNING:
        colors = Colors.WARNING;
        break;
      case Color.CANCEL:
        colors = Colors.CANCEL;
        break;
      case Color.NOT_SELECTED:
        colors = Colors.NOT_SELECTED;
        break;
      case Color.SEND:
        colors = Colors.SEND;
      default:
        colors = Colors.DEFAULT;
        break;
    }
    return colors;
  };
  const classes = useStyles(getColorsByProps());

  const renderChildren = () => {
    if (loading) return <CircularProgress className={classes.circularProgress} size={25} />;

    return children;
  };

  return (
    <ButtonMi
      disableElevation={disableElevation}
      className={classNames(classes.button, className)}
      disabled={loading || disabled}
      {...rest}
    >
      {renderChildren()}
    </ButtonMi>
  );
};

export default Button;
